<template>
  <div class="qrcode-share" :class="{ compact }">
    <div v-if="qrDataUrl" class="qrcode-box">
      <img :src="qrDataUrl" :alt="t('speechTranslation.qrcodeAlt')" class="qrcode-img" />
    </div>
    <a-button type="link" size="small" :disabled="!url" @click="copyLink" class="copy-btn">
      {{ t('speechTranslation.copyLink') }}
    </a-button>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { message } from 'ant-design-vue'
import QRCode from 'qrcode'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    url: string
    compact?: boolean
  }>(),
  { compact: false }
)

const qrDataUrl = ref('')
const qrSize = computed(() => (props.compact ? 120 : 200))

async function generateQR() {
  if (!props.url) {
    qrDataUrl.value = ''
    return
  }
  try {
    qrDataUrl.value = await QRCode.toDataURL(props.url, { width: qrSize.value, margin: 2 })
  } catch {
    qrDataUrl.value = ''
  }
}

watch(
  () => [props.url, props.compact],
  () => generateQR(),
  { immediate: true }
)

/** 使用 execCommand 回退复制（用于非安全上下文或 Clipboard API 失败时） */
function fallbackCopy(text: string): boolean {
  try {
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.left = '-9999px'
    document.body.appendChild(textArea)
    textArea.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(textArea)
    return ok
  } catch {
    return false
  }
}

/**
 * 复制文本到剪贴板。
 * 根因：navigator.clipboard 仅在安全上下文（HTTPS/localhost）可用，HTTP 下会抛错。
 * 优先使用 Clipboard API，失败时回退到 execCommand('copy')。
 */
async function copyLink() {
  if (!props.url) return
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(props.url)
      message.success(t('speechTranslation.linkCopied'))
      return
    }
  } catch (_e) {
    // Clipboard API 抛错（如权限被拒），尝试回退
  }
  if (fallbackCopy(props.url)) {
    message.success(t('speechTranslation.linkCopied'))
  } else {
    message.error(t('speechTranslation.copyFailed'))
  }
}
</script>

<style scoped>
.qrcode-share {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
}

.qrcode-share.compact {
  padding: 8px;
}

.qrcode-share.compact .qrcode-box {
  padding: 8px;
}

.qrcode-share.compact .qrcode-img {
  width: 120px;
  height: 120px;
}

.qrcode-share.compact .copy-btn {
  margin-top: 8px;
  font-size: 12px;
}

.copy-btn {
  margin-top: 12px;
  padding: 0;
  display: block;
}

.qrcode-box {
  display: inline-block;
  padding: 12px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
}
.qrcode-img {
  display: block;
  width: 200px;
  height: 200px;
}
</style>
